import React, { useState, useEffect, useRef, useCallback } from 'react'
import PropTypes from 'prop-types'
import gsap from 'gsap'

// Auto-rotate settings
const FULL_ROTATION_DURATION = 10 // Seconds for full 360° rotation per zone
const TRANSITION_DELAY = 800 // Ms to wait after reaching a new zone before rotating

/**
 * Bottom navigation dots for orbit template.
 * Supports arrow key navigation, auto-rotate with play/pause.
 */
export function OrbitNavigation({ xyzAPI })
{
    const [ currentIndex, setCurrentIndex ] = useState(0)
    const [ waypoints, setWaypoints ] = useState([])
    const [ isAutoRotating, setIsAutoRotating ] = useState(false)
    const containerRef = useRef(null)
    const dotsRef = useRef([])
    const playButtonRef = useRef(null)
    const playIconRef = useRef(null)
    const pauseIconRef = useRef(null)

    // Auto-rotate state refs (to avoid stale closures)
    const autoRotateRef = useRef({
        isPlaying: false,
        gsapTween: null,
        rotationTween: null,
        transitionTimeout: null
    })

    // Load waypoints
    useEffect(() =>
    {
        if (!xyzAPI) return
        const sceneManager = xyzAPI.getSceneManager()
        if (sceneManager)
        {
            const wp = sceneManager.waypoints || []
            setWaypoints(wp)
        }
    }, [ xyzAPI ])

    // Subscribe to camera/waypoint changes
    useEffect(() =>
    {
        if (!xyzAPI || !xyzAPI.subscribe) return

        const unsubscribe = xyzAPI.subscribe((waypoint) =>
        {
            if (waypoint && typeof waypoint.index === 'number')
            {
                setCurrentIndex(waypoint.index)
            }
        })
        return () => unsubscribe()
    }, [ xyzAPI ])

    // Stop auto-rotate and cleanup
    const stopAutoRotate = useCallback(() =>
    {
        const state = autoRotateRef.current

        if (state.rotationTween)
        {
            state.rotationTween.kill()
            state.rotationTween = null
        }
        if (state.transitionTimeout)
        {
            clearTimeout(state.transitionTimeout)
            state.transitionTimeout = null
        }

        state.isPlaying = false
        setIsAutoRotating(false)
    }, [])

    // Navigate to index
    const goToIndex = useCallback((index, fromAutoRotate = false) =>
    {
        if (!xyzAPI || waypoints.length === 0) return
        if (index < 0 || index >= waypoints.length) return

        if (!xyzAPI.goToSceneZoneByIndex) return

        // Pass false to force the transition (blocking behavior style from Presentation)
        xyzAPI.goToSceneZoneByIndex(index, false)
        setCurrentIndex(index)

        // If triggered by user (not auto-rotate), stop auto-rotation
        if (!fromAutoRotate && isAutoRotating)
        {
            stopAutoRotate()
        }
    }, [ xyzAPI, waypoints, isAutoRotating, stopAutoRotate ])

    // Navigation functions
    const goNext = useCallback(() =>
    {
        const nextIndex = (currentIndex + 1) % waypoints.length
        goToIndex(nextIndex)
    }, [ currentIndex, waypoints, goToIndex ])

    const goPrev = useCallback(() =>
    {
        const prevIndex = (currentIndex - 1 + waypoints.length) % waypoints.length
        goToIndex(prevIndex)
    }, [ currentIndex, waypoints, goToIndex ])



    // Start auto-rotate sequence
    const startAutoRotate = useCallback(() =>
    {
        if (!xyzAPI || waypoints.length === 0) return

        const state = autoRotateRef.current
        state.isPlaying = true
        setIsAutoRotating(true)

        let zoneIndex = currentIndex

        const rotateCurrentZone = () =>
        {
            if (!state.isPlaying) return

            // Rotate full 360° using GSAP
            const rotationObj = { angle: 0 }
            state.rotationTween = gsap.to(rotationObj, {
                angle: Math.PI * 2, // Full 360°
                duration: FULL_ROTATION_DURATION,
                ease: "none",
                onUpdate: () =>
                {
                    if (!state.isPlaying) return
                    const controls = xyzAPI.getControls?.()
                    if (controls)
                    {
                        // Rotate by small increments
                        controls.rotate(0.01, 0, false)
                    }
                },
                onComplete: () =>
                {
                    if (!state.isPlaying) return

                    // Move to next zone
                    zoneIndex = (zoneIndex + 1) % waypoints.length
                    goToIndex(zoneIndex, true)

                    // Wait for camera transition, then start rotating again
                    state.transitionTimeout = setTimeout(() =>
                    {
                        if (state.isPlaying)
                        {
                            rotateCurrentZone()
                        }
                    }, TRANSITION_DELAY + 1500) // Add extra time for camera transition
                }
            })
        }

        // Start the rotation sequence
        rotateCurrentZone()
    }, [ xyzAPI, waypoints, currentIndex, goToIndex ])

    // Toggle auto-rotate
    const toggleAutoRotate = useCallback(() =>
    {
        if (isAutoRotating)
        {
            stopAutoRotate()
        }
        else
        {
            startAutoRotate()
        }

        // Button bounce animation
        if (playButtonRef.current)
        {
            gsap.fromTo(playButtonRef.current,
                { scale: 0.8 },
                { scale: 1, duration: 0.4, ease: "elastic.out(1, 0.5)" }
            )
        }
    }, [ isAutoRotating, startAutoRotate, stopAutoRotate ])

    // Animate play/pause icons
    useEffect(() =>
    {
        if (playIconRef.current && pauseIconRef.current)
        {
            if (isAutoRotating)
            {
                gsap.to(playIconRef.current, { opacity: 0, scale: 0.5, duration: 0.2 })
                gsap.to(pauseIconRef.current, { opacity: 1, scale: 1, duration: 0.3, delay: 0.1 })
            }
            else
            {
                gsap.to(pauseIconRef.current, { opacity: 0, scale: 0.5, duration: 0.2 })
                gsap.to(playIconRef.current, { opacity: 1, scale: 1, duration: 0.3, delay: 0.1 })
            }
        }
    }, [ isAutoRotating ])

    // Initial button animation on mount
    useEffect(() =>
    {
        if (playButtonRef.current && waypoints.length > 0)
        {
            gsap.fromTo(playButtonRef.current,
                { scale: 0, opacity: 0 },
                { scale: 1, opacity: 1, duration: 0.5, ease: "back.out(1.7)", delay: 0.3 }
            )
        }
    }, [ waypoints ])

    // Keyboard navigation
    useEffect(() =>
    {
        const handleKeyDown = (e) =>
        {
            if (e.key === 'ArrowLeft') goPrev()
            if (e.key === 'ArrowRight') goNext()
            // Space bar toggles auto-rotate
            if (e.key === ' ')
            {
                e.preventDefault()
                toggleAutoRotate()
            }
        }
        window.addEventListener('keydown', handleKeyDown)
        return () => window.removeEventListener('keydown', handleKeyDown)
    }, [ goPrev, goNext, toggleAutoRotate ])

    // Detect user interaction to pause auto-rotate
    useEffect(() =>
    {
        if (!isAutoRotating) return

        const handleUserInteraction = (e) =>
        {
            // Ignore clicks on our navigation UI
            if (containerRef.current?.contains(e.target)) return

            // Stop auto-rotate on user interaction
            stopAutoRotate()
        }

        // Listen for mouse/touch interactions on the canvas
        window.addEventListener('mousedown', handleUserInteraction)
        window.addEventListener('touchstart', handleUserInteraction)
        window.addEventListener('wheel', handleUserInteraction)

        return () =>
        {
            window.removeEventListener('mousedown', handleUserInteraction)
            window.removeEventListener('touchstart', handleUserInteraction)
            window.removeEventListener('wheel', handleUserInteraction)
        }
    }, [ isAutoRotating, stopAutoRotate ])

    // Cleanup on unmount
    useEffect(() =>
    {
        return () =>
        {
            stopAutoRotate()
        }
    }, [ stopAutoRotate ])

    if (!xyzAPI || waypoints.length === 0) return null

    return (
        <div
            ref={containerRef}
            className="absolute bottom-8 left-1/2 -translate-x-1/2 z-50 flex flex-col items-center gap-3"
        >
            {/* Auto-rotate Play/Pause Button */}
            <button
                ref={playButtonRef}
                onClick={toggleAutoRotate}
                className={`
                    relative w-12 h-12 rounded-full 
                    bg-linear-to-br from-white/20 to-white/5
                    backdrop-blur-md border border-white/20
                    flex items-center justify-center
                    cursor-pointer select-none
                    transition-all duration-300
                    hover:scale-110 hover:border-white/40
                    hover:shadow-[0_0_20px_rgba(255,255,255,0.3)]
                    active:scale-95
                    ${isAutoRotating ? 'shadow-[0_0_15px_rgba(255,255,255,0.25)]' : ''}
                `}
                aria-label={isAutoRotating ? 'Pause auto-rotate' : 'Start auto-rotate'}
                style={{ opacity: 0 }}
            >
                {/* Play Icon */}
                <svg
                    ref={playIconRef}
                    className="absolute w-5 h-5 text-white ml-0.5"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                >
                    <path d="M8 5v14l11-7z" />
                </svg>

                {/* Pause Icon */}
                <svg
                    ref={pauseIconRef}
                    className="absolute w-5 h-5 text-white"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    style={{ opacity: 0 }}
                >
                    <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
                </svg>

                {/* Glow ring when active */}
                {isAutoRotating && (
                    <div className="absolute inset-0 rounded-full animate-pulse bg-white/10" />
                )}
            </button>

            {/* Dot container */}
            <div className="relative flex flex-wrap justify-center gap-3 px-6 py-3 bg-black/40 backdrop-blur-sm rounded-full border border-white/10">
                {/* Navigation dots - skip current index */}
                {waypoints.map((wp, i) =>
                {
                    const isActive = i === currentIndex

                    return (
                        <button
                            key={i}
                            ref={el => dotsRef.current[ i ] = el}
                            onClick={() => goToIndex(i)}
                            className={`
                                relative rounded-full 
                                transition-all duration-300 ease-out
                                ${isActive
                                    ? 'w-3 h-3 bg-white scale-110 shadow-[0_0_10px_rgba(255,255,255,0.5)]'
                                    : 'w-2 h-2 bg-white/40 hover:bg-white/70 hover:scale-150'
                                }
                            `}
                            aria-label={`Go to slide ${i + 1}`}
                            aria-current={isActive ? 'true' : 'false'}
                        />
                    )
                })}
            </div>
        </div>
    )
}

OrbitNavigation.propTypes = {
    xyzAPI: PropTypes.object
}
